import {useEffect, useMemo, useState} from "react";
import type {MenuProps} from "antd";
import {Menu} from "antd";

import {
    LayoutDashboard,
    Users as UsersIcon,
    ArrowLeftRight,
    Link2,
    Store,
    TrendingUp,
    LifeBuoy,
    Settings,
} from "lucide-react";
import {useLocation, useNavigate} from "react-router-dom";
import {useSelector} from "react-redux";
import {RootState} from "../../redux/store";
import {ADMIN_PERMISSIONS, hasAdminPermission} from "../../auth/adminPermissions";

type Permission = string | string[] | undefined;

type NavLink = {label: string; path: string; icon?: JSX.Element; permission?: Permission};
type NavGroup = {label: string; key: string; icon: JSX.Element; children: NavLink[]};
type NavEntry = NavLink | NavGroup;

const isGroup = (entry: NavEntry): entry is NavGroup => "children" in entry;

/**
 * Grouped by the thing an admin is working on. Single-destination entries stay flat —
 * burying one page under a collapsible adds a click and tells the reader nothing.
 *
 * Permissions live on the leaves. A group is only as visible as its children: if an admin
 * can reach none of them, the group itself never renders.
 */
const NAV: NavEntry[] = [
    {label: "Home", path: "/", icon: <LayoutDashboard size={16}/>, permission: ADMIN_PERMISSIONS.dashboard},

    {
        label: "Customers",
        key: "customers",
        icon: <UsersIcon size={16}/>,
        children: [
            {label: "Users", path: "/users", permission: ADMIN_PERMISSIONS.usersManage},
            {label: "Corporate Accounts", path: "/corporate-accounts", permission: ADMIN_PERMISSIONS.corporateAccountsManage},
            {label: "Group Savings", path: "/saving-groups", permission: ADMIN_PERMISSIONS.groupSavingsReports},
        ],
    },

    {
        label: "Payments",
        key: "payments",
        icon: <ArrowLeftRight size={16}/>,
        children: [
            {label: "Transactions", path: "/transactions", permission: ADMIN_PERMISSIONS.transactions},
            {label: "Lenco Transactions", path: "/lenco-transactions", permission: ADMIN_PERMISSIONS.lenco},
            {label: "Payment Requests", path: "/requests", permission: ADMIN_PERMISSIONS.groupSavingsLiquidationView},
            {label: "Bill Payments", path: "/bill-payments", permission: ADMIN_PERMISSIONS.bills},
            {
                label: "Corporate Withdrawals",
                path: "/corporate-withdrawals",
                permission: ADMIN_PERMISSIONS.groupSavingsLiquidationView,
            },
        ],
    },

    {
        label: "Payment Links",
        key: "payment-links",
        icon: <Link2 size={16}/>,
        children: [
            {label: "Reports", path: "/payment-link-reports", permission: ADMIN_PERMISSIONS.paymentLinkReports},
            {
                label: "Liquidations",
                path: "/payment-link-liquidations",
                permission: ADMIN_PERMISSIONS.paymentLinkLiquidationView,
            },
            {
                label: "Refunds",
                path: "/payment-link-refunds",
                permission: ADMIN_PERMISSIONS.paymentLinkLiquidationView,
            },
        ],
    },

    {
        label: "Merchants",
        path: "/merchants",
        icon: <Store size={16}/>,
        permission: [ADMIN_PERMISSIONS.merchantsManage, ADMIN_PERMISSIONS.merchantTransactionsView],
    },

    {label: "Investments", path: "/investments", icon: <TrendingUp size={16}/>, permission: ADMIN_PERMISSIONS.investmentsView},

    {
        label: "Support Tickets",
        path: "/support-tickets",
        icon: <LifeBuoy size={16}/>,
        permission: ADMIN_PERMISSIONS.supportTicketsView,
    },

    {
        label: "Administration",
        key: "administration",
        icon: <Settings size={16}/>,
        children: [
            {label: "Admin Users", path: "/admin-users", permission: ADMIN_PERMISSIONS.adminUsers},
            {label: "Audit Trail", path: "/audit-trails", permission: ADMIN_PERMISSIONS.auditTrail},
        ],
    },
];

/**
 * The longest nav path that prefixes the current URL, plus the group holding it. A detail
 * page like `/merchants/<id>` has no entry of its own, so without prefix matching the sidebar
 * would show nothing selected the moment one is opened.
 */
function matchPath(pathname: string, entries: NavEntry[]): {selected: string; openKey?: string} {
    let selected = pathname;
    let openKey: string | undefined;
    let bestLength = -1;

    entries.forEach((entry) => {
        const candidates = isGroup(entry)
            ? entry.children.map((child) => ({path: child.path, group: entry.key as string | undefined}))
            : [{path: entry.path, group: undefined}];

        candidates.forEach(({path, group}) => {
            const matches = path === "/"
                ? pathname === "/"
                : pathname === path || pathname.startsWith(`${path}/`);

            if (matches && path.length > bestLength) {
                bestLength = path.length;
                selected = path;
                openKey = group;
            }
        });
    });

    return {selected, openKey};
}

function Sidebar({collapsed = false}: {collapsed?: boolean}) {
    const navigate = useNavigate();
    const location = useLocation();
    const {permissions, roles} = useSelector((state: RootState) => state.account);

    const visibleNav = useMemo(() => {
        const allowed = (permission: Permission) => hasAdminPermission(permissions, roles, permission);

        return NAV.reduce<NavEntry[]>((entries, entry) => {
            if (!isGroup(entry)) {
                if (allowed(entry.permission)) entries.push(entry);
                return entries;
            }

            const children = entry.children.filter((child) => allowed(child.permission));
            if (children.length) entries.push({...entry, children});
            return entries;
        }, []);
    }, [permissions, roles]);

    const items: MenuProps["items"] = useMemo(
        () => visibleNav.map((entry) =>
            isGroup(entry)
                ? {
                    label: entry.label,
                    key: entry.key,
                    icon: entry.icon,
                    children: entry.children.map((child) => ({label: child.label, key: child.path})),
                }
                : {label: entry.label, key: entry.path, icon: entry.icon}
        ),
        [visibleNav],
    );

    const {selected, openKey} = useMemo(
        () => matchPath(location.pathname, visibleNav),
        [location.pathname, visibleNav],
    );
    const [openKeys, setOpenKeys] = useState<string[]>(openKey ? [openKey] : []);

    // Keep the group holding the current page open, without closing any the admin opened
    // themselves — collapsing a menu someone just expanded is its own small annoyance.
    useEffect(() => {
        if (openKey) {
            setOpenKeys((current) => current.includes(openKey) ? current : [...current, openKey]);
        }
    }, [openKey]);

    const onClick: MenuProps["onClick"] = (event) => {
        navigate(event.key);
    };

    return (
        <Menu
            className="text-[#7C8DB5]"
            onClick={onClick}
            selectedKeys={[selected]}
            // Collapsed, AntD renders each group as a hover flyout and drives its own open
            // state; forcing openKeys in that mode fights it and logs a warning.
            {...(collapsed ? {} : {openKeys, onOpenChange: (keys: string[]) => setOpenKeys(keys)})}
            inlineCollapsed={collapsed}
            mode="inline"
            items={items}
        />
    );
}

export default Sidebar;
